import {
  ChevronDown,
  ChevronRight,
  Folder,
  MessagesSquare,
  Plus,
  Search,
  SquarePen
} from "lucide-react";
import { AgentMark, Identicon } from "./AgentMark.tsx";
import type { DemoTarget, ReplicaPerson, ReplicaState } from "@/product-stage/demo/types.ts";
import { sceneForAgent } from "@/product-stage/demo/world.ts";

export function SidebarReplica({
  state,
  onSelect
}: {
  state: ReplicaState;
  onSelect(target: DemoTarget): void;
}) {
  return (
    <aside className="app-sidebar">
      <div className="sidebar-brand">
        <img className="brand-mark" src={`${import.meta.env.BASE_URL}matterline.svg`} alt="" />
        <div>
          <strong>Matterline</strong>
          <small>智能工作台</small>
        </div>
      </div>

      <div className="sidebar-search-row">
        <button type="button" className="sidebar-search" tabIndex={-1}>
          <Search aria-hidden />
          <span>搜索消息和项目</span>
          <kbd>⌘K</kbd>
        </button>
        <button type="button" className="sidebar-create" aria-label="新建" tabIndex={-1}>
          <Plus aria-hidden />
        </button>
      </div>

      <div className="sidebar-overview">
        <section className="sidebar-section" data-section="inbox">
          <div className="sidebar-section-head">
            <h2>收件箱</h2>
            <button type="button" className="sidebar-section-icon" aria-label="新消息" tabIndex={-1}>
              <SquarePen />
            </button>
          </div>
          <div className="sidebar-section-list">
            {state.directChats.map((chat) => {
              const person = personById(state, chat.personId);
              return (
                <button
                  key={chat.id}
                  type="button"
                  className="direct-chat-item"
                  data-active={state.active.directChatId === chat.id}
                  onClick={() => onSelect({ kind: "scene", sceneId: chat.sceneId })}
                >
                  <Identicon seed={person.seed} className="direct-chat-avatar" />
                  <span className="direct-chat-copy">
                    <span>
                      <strong>{person.name}</strong>
                      <time>{chat.time}</time>
                    </span>
                    <small>{chat.preview}</small>
                  </span>
                  {chat.unread > 0 ? <i className="direct-chat-unread">{chat.unread}</i> : null}
                </button>
              );
            })}
          </div>
          <More label="全部私信" />
        </section>

        <section className="sidebar-section" data-section="threads">
          <div className="sidebar-section-head">
            <h2>线程</h2>
          </div>
          <div className="sidebar-section-list">
            {state.threads.map((thread) => (
              <button
                key={thread.id}
                type="button"
                className="sidebar-thread-item"
                data-active={
                  (state.view === "thread" && state.active.threadId === thread.id) ||
                  (state.view === "meeting" && thread.sceneId === "meeting-review")
                }
                onClick={() => onSelect({ kind: "scene", sceneId: thread.sceneId })}
              >
                <MessagesSquare aria-hidden />
                <span>
                  <strong>{thread.title}</strong>
                  <small>{thread.room}</small>
                </span>
                <time>{thread.time}</time>
              </button>
            ))}
          </div>
          <More label="全部线程" />
        </section>

        <section className="sidebar-section" data-section="projects">
          <div className="sidebar-section-head">
            <h2>项目</h2>
            <button type="button" className="sidebar-section-icon" aria-label="新建项目" tabIndex={-1}>
              <Plus />
            </button>
          </div>
          <div className="sidebar-section-list">
            {state.projects.map((project) => (
              <button
                key={project.id}
                type="button"
                className="space-item"
                data-compact="true"
                data-active={state.active.projectId === project.id && !state.active.files && state.view !== "direct"}
                onClick={() => onSelect({ kind: "tab", tab: "threads" })}
              >
                <span className="space-hash" data-tone={project.tone} aria-hidden>
                  #
                </span>
                <span className="space-copy">
                  <strong>{project.name}</strong>
                </span>
                {state.active.projectId === project.id && state.active.threadId ? (
                  <i className="space-active-dot" />
                ) : null}
              </button>
            ))}
          </div>
          <More label="全部项目" />
        </section>

        <section className="sidebar-section" data-section="agents">
          <div className="sidebar-section-head">
            <h2>智能体</h2>
          </div>
          <div className="sidebar-section-list">
            {state.agents.map((agent) => (
              <button
                key={agent.id}
                type="button"
                className="space-item agent-directory-item"
                data-compact="true"
                data-active={state.active.agentId === agent.id}
                onClick={() => onSelect({ kind: "scene", sceneId: sceneForAgent(agent.id) })}
              >
                <AgentMark agent={agent} variant="sidebar" />
                <span className="space-copy">
                  <strong>{agent.name}</strong>
                  <small>{agent.status === "working" ? "工作中" : agent.blurb}</small>
                </span>
              </button>
            ))}
          </div>
          <More label="全部智能体" />
        </section>
      </div>

      <div className="sidebar-dock">
        <div className="sidebar-files">
          <button
            type="button"
            className="sidebar-file-link"
            data-active={state.active.files}
            onClick={() => onSelect({ kind: "scene", sceneId: "project-files" })}
          >
            <span className="sidebar-file-mark" aria-hidden>
              <Folder />
            </span>
            <strong>文件</strong>
          </button>
        </div>
        <div className="sidebar-footer">
          <div className="sidebar-account">
            <div className="account-button">
              <Identicon seed={state.account.name} className="account-avatar" />
              <span className="account-copy">
                <strong>{state.account.name}</strong>
              </span>
              <ChevronDown aria-hidden />
            </div>
          </div>
        </div>
      </div>
    </aside>
  );
}

function personById(state: ReplicaState, id: string): ReplicaPerson {
  const person = state.people.find((item) => item.id === id);
  if (!person) throw new Error(`Unknown person ${id}`);
  return person;
}

function More({ label }: { label: string }) {
  return (
    <button type="button" className="sidebar-section-more" tabIndex={-1}>
      <span>{label}</span>
      <ChevronRight aria-hidden />
    </button>
  );
}


